/**
 * Timesheet — same-origin OData V4 client for the CATS-backed S/4 service.
 *
 * Phase 3 (Option A). Talks to the RAP OData V4 service Phase 2 published at
 * `VITE_TS_ODATA_BASE`:
 *   /sap/opu/odata4/sap/zui_m12_timesheet_o4/srvd/sap/z_m12_timesheet_sd/0001
 *
 * The SPA is SAME-ORIGIN with this service (it is hosted as a BSP in Phase 4),
 * so authentication is the SAP session cookie — NO bearer token, NO proxy key.
 * Modifying requests (bound-action POST, `$batch`) carry an `X-CSRF-Token`
 * fetched once via a `HEAD` with `X-CSRF-Token: Fetch`. Reads are plain GETs.
 * In dev the `/sap` Vite proxy in `vite.config.s4.ts` (creds from
 * `.env.s4.local`) makes the same paths reachable from localhost.
 *
 * The client is deliberately tiny and pure-ish: the network primitive is
 * injectable via `__setOdataFetch` so the contract tests can assert the exact
 * URL / method / headers / body the adapter emits without a live SAP system.
 *
 * See: docs/architecture/handoffs/PHASE_3_HANDOFF.md §3 / §4 / §9.
 */

/** UI OData V4 base (same-origin). Falls back to the published path when the
 *  env var is absent (e.g. under vitest, which does not load `.env.s4`). */
export const TS_ODATA_BASE: string =
  ((import.meta.env.VITE_TS_ODATA_BASE as string | undefined) ??
    '/sap/opu/odata4/sap/zui_m12_timesheet_o4/srvd/sap/z_m12_timesheet_sd/0001').replace(/\/+$/, '')

/**
 * Schema namespace that prefixes the bound action names in the URL. Read off
 * `$metadata` (Schema Namespace / SAP__self alias) rather than invented; it is
 * stable for the published service. Overridable via env so a re-versioned
 * service does not require a code change.
 */
export const TS_ACTION_NAMESPACE: string =
  (import.meta.env.VITE_TS_ODATA_NAMESPACE as string | undefined) ??
  'com.sap.gateway.srvd.z_m12_timesheet_sd.v0001'

// ─── Injectable fetch (test seam) ────────────────────────────────────────────

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>

let _fetch: FetchLike = (input, init) => (globalThis.fetch as FetchLike)(input, init)

/** Swap the network primitive (contract tests). Passing `null` restores the
 *  global fetch. Resets the cached CSRF token so each test starts clean. */
export function __setOdataFetch(fn: FetchLike | null): void {
  _fetch = fn ?? ((input, init) => (globalThis.fetch as FetchLike)(input, init))
  _csrfToken = null
}

// ─── CSRF handling ───────────────────────────────────────────────────────────

let _csrfToken: string | null = null

/** Reset the cached CSRF token (after a 403 "Required", or between tests). */
export function resetCsrfToken(): void {
  _csrfToken = null
}

/**
 * Fetch (and cache) the X-CSRF-Token for modifying requests. Same-origin, so
 * the session cookie rides along automatically (`credentials: 'include'`).
 * A `HEAD` on the service root with `X-CSRF-Token: Fetch` returns the token in
 * the response header. Never throws: a system that does not require CSRF simply
 * yields an empty token (the POST then succeeds without one).
 */
async function ensureCsrfToken(): Promise<string> {
  if (_csrfToken !== null) return _csrfToken
  try {
    const res = await _fetch(`${TS_ODATA_BASE}/`, {
      method: 'HEAD',
      headers: { 'X-CSRF-Token': 'Fetch', Accept: 'application/json' },
      credentials: 'include',
    })
    const token = res.headers.get('x-csrf-token') ?? res.headers.get('X-CSRF-Token')
    _csrfToken = token && token.toLowerCase() !== 'required' ? token : ''
  } catch {
    _csrfToken = ''
  }
  return _csrfToken
}

// ─── Reads ───────────────────────────────────────────────────────────────────

export interface ODataQuery {
  filter?: string
  select?: readonly string[]
  orderby?: string
  top?: number
  skip?: number
}

function buildQueryString(q: ODataQuery): string {
  const params = new URLSearchParams()
  if (q.filter) params.set('$filter', q.filter)
  if (q.select && q.select.length) params.set('$select', q.select.join(','))
  if (q.orderby) params.set('$orderby', q.orderby)
  if (q.top != null) params.set('$top', String(q.top))
  if (q.skip != null) params.set('$skip', String(q.skip))
  const s = params.toString()
  return s ? `?${s}` : ''
}

/** GET an entity set on the timesheet service and return its `value` array. */
/**
 * Pages to follow before giving up. The gateway returns 100 rows a page, so this
 * reads up to 5,000 rows. A cap exists so a mistaken filter cannot spin forever
 * against a large entity.
 */
const MAX_PAGES = 50

/**
 * GET a collection, following @odata.nextLink to the end.
 *
 * The gateway caps a response at 100 rows and returns a nextLink for the rest.
 * Ignoring it truncated every list in the app silently: the employee pickers saw
 * the first 100 of 1,207 people, so searching for anyone further down the
 * alphabet found nothing and looked like missing data rather than a short read.
 */
export async function odataList<T>(entitySet: string, query: ODataQuery = {}): Promise<T[]> {
  let url: string | null = `${TS_ODATA_BASE}/${entitySet}${buildQueryString(query)}`
  const out: T[] = []

  for (let page = 0; url && page < MAX_PAGES; page++) {
    const res: Response = await _fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    })
    if (!res.ok) throw new Error(`[cats] GET ${entitySet} failed (${res.status})`)
    const body = (await res.json()) as { value?: T[]; '@odata.nextLink'?: string }
    if (body.value?.length) out.push(...body.value)
    const next = body['@odata.nextLink']
    // The gateway returns nextLink relative to the service root on this system,
    // so resolve it rather than assuming it is absolute.
    url = next ? (next.startsWith('http') ? next : `${TS_ODATA_BASE}/${next.replace(/^\/+/, '')}`) : null
  }
  return out
}

/** GET an ABSOLUTE same-origin OData collection URL (a sibling ZUI_M12_* read
 *  service, e.g. WBS / production orders / personnel). Returns `value`. */
export async function odataListAt<T>(fullUrl: string, query: ODataQuery = {}): Promise<T[]> {
  const url = `${fullUrl}${buildQueryString(query)}`
  const res = await _fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  if (!res.ok) throw new Error(`[cats] GET ${fullUrl} failed (${res.status})`)
  const body = (await res.json()) as { value?: T[] }
  return body.value ?? []
}

// ─── Bound actions (the write path) ──────────────────────────────────────────

/** URL for a bound static action on an entity SET (collection binding). Phase 2
 *  actions are RAP static actions, so they bind to the collection, not a key. */
export function actionUrl(entitySet: string, action: string): string {
  return `${TS_ODATA_BASE}/${entitySet}/${TS_ACTION_NAMESPACE}.${action}`
}

async function postWithCsrf(url: string, jsonBody: unknown): Promise<Response> {
  const body = JSON.stringify(jsonBody)
  const send = (token: string) =>
    _fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': token,
      },
      credentials: 'include',
      body,
    })
  let res = await send(await ensureCsrfToken())
  // Token expired / server wants a fresh one: refetch once and retry.
  if (res.status === 403 && (res.headers.get('x-csrf-token') ?? '').toLowerCase() === 'required') {
    resetCsrfToken()
    res = await send(await ensureCsrfToken())
  }
  return res
}

/** POST a bound action with the parameters as the JSON body. Returns the parsed
 *  response body (an updated TimeSheet entity, or `undefined` for 204). */
export async function invokeAction<T = unknown>(
  entitySet: string,
  action: string,
  params: object,
): Promise<T | undefined> {
  const res = await postWithCsrf(actionUrl(entitySet, action), params)
  if (!res.ok) {
    const detail = await safeErrorText(res)
    throw new Error(`[cats] action ${action} failed (${res.status})${detail}`)
  }
  const text = await res.text()
  return (text ? (JSON.parse(text) as T) : undefined)
}

// ─── $batch (JSON batch) ─────────────────────────────────────────────────────

export interface BatchRequest {
  id: string
  method: 'GET' | 'POST'
  /** URL relative to the service root, e.g. `TimeSheet/<ns>.submit`. */
  url: string
  headers?: Record<string, string>
  body?: unknown
}

export interface BatchResponse {
  id: string
  status: number
  body?: unknown
}

/**
 * POST a JSON `$batch` (OData V4). Used to persist a week's changes (the CATS
 * records via `createEntry` + the release/overlay via `submit`) in ONE round
 * trip, matching `submitWeek`'s transactional intent. CSRF-guarded like any
 * modifying request.
 */
export async function odataBatch(requests: BatchRequest[]): Promise<BatchResponse[]> {
  const res = await postWithCsrf(`${TS_ODATA_BASE}/$batch`, { requests })
  if (!res.ok) {
    const detail = await safeErrorText(res)
    throw new Error(`[cats] $batch failed (${res.status})${detail}`)
  }
  const body = (await res.json()) as { responses?: BatchResponse[] }
  return body.responses ?? []
}

/**
 * The readable message in an error body, whichever hop wrote it:
 *   - SAP V4:                 {"error":{"message":"..."}}
 *   - the BTP proxy (hosted): {"error":"s4_error","detail":{"error":{"message":"..."}}}
 *   - the hosted API's guard: {"error":"timesheet_sap_refused","message":"..."}
 */
export function odataErrorMessage(body: unknown): string | null {
  const j = body as { error?: unknown; message?: unknown; detail?: unknown } | null
  if (!j || typeof j !== 'object') return null
  const sap = (x: unknown): string | null => {
    const m = (x as { error?: { message?: unknown } } | null)?.error?.message
    return typeof m === 'string' && m ? m : null
  }
  return sap(j) ?? sap(j.detail) ?? (typeof j.message === 'string' && j.message ? j.message : null)
}

async function safeErrorText(res: Response): Promise<string> {
  try {
    const text = await res.text()
    if (!text) return ''
    try {
      const msg = odataErrorMessage(JSON.parse(text))
      if (msg) return `: ${msg}`
    } catch {
      /* not JSON */
    }
    return `: ${text.slice(0, 300)}`
  } catch {
    return ''
  }
}

// ─── Entity-level writes (managed RAP BOs) ───────────────────────────────────
//
// Bound actions above cover the unmanaged timesheet BO. The admin config BOs
// (ChargeObjectApprover, CatsProfileByEmployeeGroup) are MANAGED RAP objects,
// so they are maintained with ordinary OData entity CRUD rather than actions.

/** Key URL for a managed entity, e.g. Foo(A='1',B='2'). Values are quoted as
 *  OData string literals, which is what every key on these config BOs is. */
/**
 * A key value that must appear unquoted in the URL.
 *
 * OData V4 quotes Edm.String keys and rejects quotes on Edm.Date, Edm.Guid and
 * the numeric types. A DATS key such as ApprovalDelegation.ValidFrom comes back
 * as Edm.Date, so `ValidFrom='2026-09-28'` is a 400 while `ValidFrom=2026-09-28`
 * is correct. Callers mark those explicitly rather than the builder guessing
 * from the shape of the value.
 */
export type ODataKeyValue = string | { raw: string }

export const rawKey = (v: string): ODataKeyValue => ({ raw: v })

export function entityKeyUrl(entitySet: string, keys: Record<string, ODataKeyValue>): string {
  const inner = Object.entries(keys)
    .map(([k, v]) =>
      typeof v === 'object' && v !== null && 'raw' in v
        ? `${k}=${v.raw}`
        : `${k}='${String(v).replace(/'/g, "''")}'`)
    .join(',')
  return `${TS_ODATA_BASE}/${entitySet}(${inner})`
}

async function writeJson(url: string, method: 'POST' | 'PATCH' | 'DELETE', body?: unknown) {
  const send = async (token: string) => {
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (token) headers['X-CSRF-Token'] = token
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    return _fetch(url, {
      method,
      headers,
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
  }

  let res = await send(await ensureCsrfToken())
  // A stale token comes back as 403; refetch once and retry before giving up.
  if (res.status === 403) {
    resetCsrfToken()
    res = await send(await ensureCsrfToken())
  }
  if (!res.ok) {
    let detail = ''
    try {
      const msg = odataErrorMessage(await res.json())
      detail = msg ? ` - ${msg}` : ''
    } catch { /* non-JSON error body */ }
    throw new Error(`[cats] ${method} ${url} failed (${res.status})${detail}`)
  }
  if (res.status === 204) return null
  try { return await res.json() } catch { return null }
}

/** Create one entity in a managed collection. */
export async function odataCreate<T>(entitySet: string, body: Record<string, unknown>): Promise<T | null> {
  return (await writeJson(`${TS_ODATA_BASE}/${entitySet}`, 'POST', body)) as T | null
}

/** Update one entity by key. */
export async function odataPatch<T>(
  entitySet: string,
  keys: Record<string, ODataKeyValue>,
  body: Record<string, unknown>,
): Promise<T | null> {
  return (await writeJson(entityKeyUrl(entitySet, keys), 'PATCH', body)) as T | null
}

/** Delete one entity by key. */
export async function odataDelete(entitySet: string, keys: Record<string, ODataKeyValue>): Promise<void> {
  await writeJson(entityKeyUrl(entitySet, keys), 'DELETE')
}
